import {
  femaleOnlyTag,
  maleOnlyTag,
  PROFILE_NODE_NAMES,
} from '@/services/constraints/constants'

import { VariableType } from '../models/enums'

export const trackingCycleSingularRegex =
  /^tracking-cycle=1-(day|week|month|year)$/

// the count must bigger than 1 and the unit must be plural
export const trackingCyclePluralRegex =
  /^tracking-cycle=(?:[2-9]\d*|[1-9]\d+)-(days|weeks|months|years)$/

export const trackingCycleNeverRegex = /^tracking-cycle=never$/

// the count must be 1 and the unit must be singular
export const timeframeSingularRegex = /^timeframe=1-(day|week|month|year)$/

// the count must be bigger than 1 and the unit must be plural
export const timeframePluralRegex =
  /^timeframe=(?:[2-9]\d*|[1-9]\d+)-(days|weeks|months|years)$/

export const calculationRegex = /^calculation=(addition|average)$/

export const atcRegex = /^atc=([a-zA-Z0-9]{1,7}|none)$/i

// Matches any tracking-cycle tag except "never", including malformed values —
// so a variable with an invalid tracking-cycle still surfaces its dependent
// timeframe/calculation/semicontinuity errors in the same release attempt,
// and new tracking-cycle formats count as tracked without touching this.
function hasNonNeverTrackingCycle(tags: string[]): boolean {
  return tags.some(
    (tag) =>
      KNOWN_TAGS.trackingCycle.baseOfTheTag.test(tag) &&
      !trackingCycleNeverRegex.test(tag),
  )
}

// The end-user app combines a node's tracked answers over its timeframe with
// its calculation, so a calculation without both periods has nothing to apply
// to — whatever the node's type.
function hasPublicCalculationTag(tags: string[]): boolean {
  return (
    hasPublicTag(tags) &&
    tags.some((tag) => KNOWN_TAGS.calculation.baseOfTheTag.test(tag))
  )
}

export function hasPublicTag(tags: string[]): boolean {
  return tags.some((tag) =>
    KNOWN_TAGS.public.regExps.some((regExp) => regExp.test(tag)),
  )
}

const TIMEFRAME_REQUIRED_VARIABLE_TYPES: string[] = [
  VariableType.Test,
  VariableType.MedicalProcedure,
  VariableType.Symptom,
  VariableType.Etiology,
]

// Every public node of these types describes something observed over a period,
// whether or not it is tracked — Ancestra Health needs the timeframe to
// interpret the value. Profile nodes (Sex, Age) are the exception: they state
// who the user is, so no timeframe applies.
export function requiresTimeframeTag(
  tags: string[],
  variableTypeName: string,
  variableName: string,
): boolean {
  return (
    (hasPublicTag(tags) &&
      TIMEFRAME_REQUIRED_VARIABLE_TYPES.includes(variableTypeName) &&
      !PROFILE_NODE_NAMES.includes(variableName)) ||
    hasPublicCalculationTag(tags)
  )
}

export function isTrackedPublicSymptom(
  tags: string[],
  variableTypeName: string,
): boolean {
  return (
    hasPublicTag(tags) &&
    variableTypeName === VariableType.Symptom &&
    hasNonNeverTrackingCycle(tags)
  )
}

// `singleValued` keyed tags describe one property of the node, so a second one
// contradicts the first with no rule for which wins — flagged at release
// (KBE-896). `atc` is deliberately not marked: a medication can carry several
// ATC codes.
export const KNOWN_TAGS = {
  public: {
    name: 'public',
    regExps: [/^public$/],
  },
  intervention: {
    name: 'intervention',
    regExps: [/^intervention$/],
  },
  trackingCycle: {
    name: 'tracking-cycle',
    regExps: [
      trackingCycleSingularRegex,
      trackingCyclePluralRegex,
      trackingCycleNeverRegex,
    ],
    baseOfTheTag: /^tracking-cycle/,
    singleValued: true,
    requiredWithVariableTypes: [
      VariableType.Test,
      VariableType.Symptom,
    ] as string[],
    checkIsRequired: (tags: string[], variableTypeName: string) =>
      (hasPublicTag(tags) &&
        KNOWN_TAGS.trackingCycle.requiredWithVariableTypes.includes(
          variableTypeName,
        )) ||
      hasPublicCalculationTag(tags),
  },
  timeframe: {
    name: 'timeframe',
    regExps: [timeframeSingularRegex, timeframePluralRegex],
    baseOfTheTag: /^timeframe/,
    singleValued: true,
    checkIsRequired: requiresTimeframeTag,
  },
  calculation: {
    name: 'calculation',
    regExps: [calculationRegex],
    baseOfTheTag: /^calculation/,
    singleValued: true,
    checkIsRequired: isTrackedPublicSymptom,
  },
  trackerOnly: {
    name: 'tracker-only',
    regExps: [/^tracker-only$/],
  },
  noIntervention: {
    name: 'no-intervention',
    regExps: [/^no-intervention$/],
  },
  atc: {
    name: 'atc',
    regExps: [atcRegex],
    baseOfTheTag: /^atc/i,
    requiredWithVariableTypes: [
      VariableType.MedicationOrSupplement,
    ] as string[],
    checkIsRequired: (tags: string[], variableTypeName: string) =>
      hasPublicTag(tags) &&
      KNOWN_TAGS.atc.requiredWithVariableTypes.includes(variableTypeName),
  },
  homeTest: {
    name: 'home-test',
    regExps: [/^home-test$/],
  },
  maleOnly: {
    name: maleOnlyTag,
    regExps: [new RegExp(`^${maleOnlyTag}$`)],
  },
  femaleOnly: {
    name: femaleOnlyTag,
    regExps: [new RegExp(`^${femaleOnlyTag}$`)],
  },
} as const
